import type { CSSProperties, HTMLAttributes } from "react";

import React from "react";

import twMerge from "@replo/design-system/utils/twMerge";
import {
  ChevronLeft,
  ChevronRight,
  Lock,
  RefreshCw,
  Shield,
} from "lucide-react";

const BROWSER_CONFIG = {
  HEADER_HEIGHT: 52,
  BORDER_RADIUS: 12,
  BROWSER_WIDTH: 1440,
} as const;

export interface DesktopProps extends HTMLAttributes<HTMLDivElement> {
  url?: string;
  width?: number | string;
  height?: number | string;
  layoutClassName?: string;
  UNSAFE_className?: string;
  children?: React.ReactNode;
  frameClassName?: string;
  /** Scale factor for the frame header/UI to match content scale. */
  frameScale?: number;
}

export const Desktop = React.forwardRef<HTMLDivElement, DesktopProps>(
  (
    {
      url,
      width,
      height,
      layoutClassName,
      UNSAFE_className,
      className,
      children,
      frameClassName,
      frameScale = 1,
      ...props
    },
    ref,
  ) => {
    const FRAME_BG = frameClassName ?? "bg-[var(--color-white)]";
    const HEADER_INNER_BG = "bg-surface";
    const CONTENT_BG = "bg-white";
    const URL_BAR_BG = "bg-[var(--color-sheet)]";
    const TEXT_CLASS = "text-muted";

    const cssVars: CSSProperties = {
      ["--header-h" as any]: `${BROWSER_CONFIG.HEADER_HEIGHT * frameScale}px`,
    };

    const contentAreaStyle: CSSProperties = {
      height: "calc(100% - var(--header-h))",
      borderBottomLeftRadius: `${BROWSER_CONFIG.BORDER_RADIUS * frameScale}px`,
      borderBottomRightRadius: `${BROWSER_CONFIG.BORDER_RADIUS * frameScale}px`,
    };

    return (
      <div
        ref={ref}
        className={twMerge(
          "relative",
          layoutClassName,
          UNSAFE_className,
          className,
        )}
        style={{
          ...(width != null ? { width } : {}),
          ...(height != null ? { height } : {}),
          ...cssVars,
        }}
        {...props}
      >
        <div
          className={twMerge(
            "relative w-full h-full overflow-hidden",
            FRAME_BG,
          )}
          style={{
            borderRadius: `${BROWSER_CONFIG.BORDER_RADIUS * frameScale}px`,
          }}
        >
          {/* Header: visually scaled to match content scale */}
          <div
            className={twMerge(
              "relative w-full border-b border-border",
              HEADER_INNER_BG,
            )}
            style={{ height: `calc(var(--header-h))` }}
          >
            <div
              className="origin-top-left"
              style={{
                transform: `scale(${frameScale})`,
                transformOrigin: "top left",
                width: BROWSER_CONFIG.BROWSER_WIDTH,
              }}
            >
              {/* Traffic lights */}
              <div className="absolute left-4 top-[26px] -translate-y-1/2 flex gap-2">
                <div className="w-3 h-3 rounded-full bg-[#FF5F57]" />
                <div className="w-3 h-3 rounded-full bg-[#FFBD2E]" />
                <div className="w-3 h-3 rounded-full bg-[#28C840]" />
              </div>
              {/* URL bar */}
              <div className="absolute left-[286px] right-[250px] top-[14px] h-6">
                <div
                  className={twMerge(
                    "w-full h-full rounded-md flex items-center justify-center",
                    URL_BAR_BG,
                  )}
                >
                  {url && (
                    <div
                      className={twMerge(
                        "inline-flex items-center gap-2 text-[12px] leading-[12px]",
                        TEXT_CLASS,
                      )}
                    >
                      <Lock className="w-3 h-3" />
                      <span className="truncate max-w-[420px]">{url}</span>
                    </div>
                  )}
                </div>
              </div>
              {/* Toolbar icons */}
              <div className="absolute left-[100px] top-[16px]">
                <ChevronLeft className={TEXT_CLASS} width={16} height={16} />
              </div>
              <div className="absolute left-[124px] top-[16px]">
                <ChevronRight className={TEXT_CLASS} width={16} height={16} />
              </div>
              <div className="absolute right-[200px] top-[18px] flex gap-7 text-muted">
                <Shield width={16} height={16} />
              </div>
              <div className="absolute right-[250px] top-[18px]">
                <RefreshCw className={TEXT_CLASS} width={16} height={16} />
              </div>
            </div>
          </div>

          {/* Content area */}
          <div
            className={twMerge("relative w-full overflow-hidden", CONTENT_BG)}
            style={contentAreaStyle}
          >
            {children}
          </div>
        </div>
      </div>
    );
  },
);

Desktop.displayName = "Desktop";
